import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'statdog.shotLocationCapture';
const CHANGE_EVENT = 'statdog:shot-location-capture';

const readEnabled = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
};

/**
 * Whether live stat capture shows the shot-location field. On by default;
 * stat takers who find it distracting can collapse it. Stored per device and
 * kept in sync across every picker on the page.
 */
const useShotLocationCapture = (): [boolean, (enabled: boolean) => void] => {
  const [enabled, setEnabledState] = useState<boolean>(readEnabled);

  useEffect(() => {
    const sync = () => setEnabledState(readEnabled());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      // storage unavailable — still update this session
    }
    setEnabledState(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [enabled, setEnabled];
};

export default useShotLocationCapture;
